import httpMethod from 'services/update2025/httpMethod';
import BlockDetail from "components/block-detail/BlockDetail";
import { debounce } from "lodash";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { findDivisions, searchDivisions } from "services/DivisionService";
import { findHistoryInfoCard, findHistoryInfoPerson, findInfoCard, findPersonInfos } from "services/PersonInfoService";
import { findUnits } from "services/UnitService";
import { X_TOTAL_COUNT } from "utils/constants";
import { checkDateMonth, dateFormat1, dateFormat2, range2 } from "utils/date";
import { handleError } from "utils/handleError";
import { PersonInfoContext } from "./PersonInfoContext";
import { PersonInfoEditor } from "./PersonInfoEditor";
import { PersonInfoHistory } from "./PersonInfoHistory";
import { AutoComplete } from "primereact/autocomplete";

export const PersonInfoList = () => {
    const avatar = process.env.PUBLIC_URL + "/assets/layout/images/avatar.png";

    const toast = useRef(null);
    const dt = useRef(null);
    const { t, i18n } = useTranslation(["person-info", "common"]);
    const tokenObj = httpMethod.getTokenStorage(); // { access_token, … }
    const accessToken = tokenObj?.access_token;;

    let headers = {
        Authorization: `Bearer ${accessToken}`,
        "Accept-Language": i18n.language,
    };

    const personInfoContext = useContext(PersonInfoContext);
    const {
        personInfos,
        setPersonInfos,
        personInfo,
        setPersonInfo,
        setPersonInfoDialog,
        personInfoDialog,
        personInfoDetail,
        setPersonInfoDetail,
        infoHistoryCards,
        setInfoHistoryCards,
        infoHistoryPersons,
        setInfoHistoryPersons,
        changeType,
        setChangeType,
        setPersonInfoHistoryDialog,
        personInfoHistoryDialog,
    } = personInfoContext;

    const [loading, setLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [checkLoad, setCheckLoad] = useState(false);
    const [selectedPerson, setSelectedPerson] = useState(null);
    const [divisions, setDivisions] = useState([]);
    const [selectDivision, setSelectDivision] = useState(null);

    const debounceSearchDivision = useCallback(
        debounce((valueText) => searchDivision(valueText), 300),
        []
    );

    const now = new Date();

    const beforeNow = new Date(new Date().setDate(new Date().getDate() - range2));

    const [formatDateVal, setFormatDateVal] = useState(dateFormat1);

    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 0,
        filters: {
            idNumber: { value: "", matchMode: "contains" },
            phoneNumber: { value: "", matchMode: "contains" },
            fullName: { value: "", matchMode: "contains" },
            birthDate: { value: null, matchMode: "equals" },
            addressId: { value: null, matchMode: "equals" },
            idNumberOld: { value: null, matchMode: "equals" },
            fatherIdNumber: { value: null, matchMode: "contains" },
            fatherName: { value: null, matchMode: "contains" },
            motherIdNumber: { value: null, matchMode: "contains" },
            motherName: { value: null, matchMode: "contains" },
            fromDate: { value: beforeNow, matchMode: "equals" },
            toDate: { value: now, matchMode: "equals" },
        },
    });

    useEffect(() => {
        searchDivision();
    }, []);

    useEffect(() => {
        if (checkLoad) {
            loadLazyData();
            setCheckLoad(false);
        }
    }, [lazyParams]);

    useEffect(() => {
        if (selectedPerson && selectedPerson?.idNumber) {
            findInfoCard(headers, selectedPerson.idNumber).then((res) => {
                const dataRes = res.data;
                setPersonInfo(dataRes);
            });

            findHistoryInfoPerson(headers, selectedPerson.idNumber).then((res) => {
                const dataRes = res.data;
                let dataFinal = [];
                dataRes?.forEach((e, i) => {
                    if (e.contentChanges) {
                        dataFinal.push(
                            ...Object.entries(JSON.parse(e.contentChanges)).map((x, y) => {
                                const values = x[1]?.split("->");
                                return {
                                    field: x[0],
                                    valueBefore: values[0],
                                    valueAfter: values[1],
                                    idNumber: e.idNumber,
                                    changeDate: e.changeDate,
                                    peopleChange: e.peopleChange,
                                    updatedSource: e.updatedSource,
                                };
                            })
                        );
                    }
                });
                dataFinal.forEach((e, i) => {
                    e.stt = i + 1;
                });
                setInfoHistoryPersons(dataFinal);
            });
            findHistoryInfoCard(headers, selectedPerson.idNumber).then((res) => {
                const dataRes = res.data;
                let dataFinal = [];
                dataRes?.forEach((e, i) => {
                    if (e.contentChanges) {
                        dataFinal.push(
                            ...Object.entries(JSON.parse(e.contentChanges)).map((x, y) => {
                                const values = x[1]?.split("->");
                                return {
                                    field: x[0],
                                    valueBefore: values[0],
                                    valueAfter: values[1],
                                    idNumber: e.idNumber,
                                    changeDate: e.changeDate,
                                    peopleChange: e.peopleChange,
                                    updatedSource: e.updatedSource,
                                };
                            })
                        );
                    }
                });
                dataFinal.forEach((e, i) => {
                    e.stt = i + 1;
                });
                setInfoHistoryCards(dataFinal);
            });
        } else {
            setPersonInfo(null);
            setInfoHistoryCards([]);
            setInfoHistoryPersons([]);
        }
    }, [selectedPerson]);

    const searchDivision = (text = null, page = 0, rows = 30) => {
        if (text) {
            searchDivisions(headers, text).then((res) => {
                setDivisions(
                    res.data?.map((e) => {
                        return { label: e.fullName, value: e.id };
                    })
                );
            });
        } else {
            findDivisions(headers, {
                first: page,
                rows,
                page,
                filters: {
                    // name: { value: null, matchMode: "contains" },
                },
            })
                .then((res) => {
                    setDivisions(
                        res.data?.map((e) => {
                            return { label: e.name, value: e.id };
                        })
                    );
                })
                .catch((error) => {
                    handleError(toast, t, error);
                });
        }
    };

    const loadLazyData = () => {
        setLoading(true);
        setSelectedPerson(null);
        findPersonInfos(headers, lazyParams)
            .then((res) => {
                setTotalRecords(res.headers[X_TOTAL_COUNT] ? parseInt(res.headers[X_TOTAL_COUNT]) : 0);
                const dataRes = res.data;
                dataRes?.forEach((e, i) => {
                    e.stt = lazyParams.rows * lazyParams.page + 1 + i;
                });
                if (dataRes.length > 0) {
                    setSelectedPerson(dataRes[0]);
                }
                setPersonInfos(dataRes);
            })
            .catch((error) => handleError(toast, t, error))
            .finally(() => setLoading(false));
    };

    const onPage = (event) => {
        setCheckLoad(true);
        let _lazyParams = { ...lazyParams, ...event };
        setLazyParams(_lazyParams);
    };

    const onSort = (event) => {
        setCheckLoad(true);
        let _lazyParams = { ...lazyParams, ...event };
        setLazyParams(_lazyParams);
    };

    const onFilter = (event) => {
        setCheckLoad(true);
        let _lazyParams = { ...lazyParams, ...event };
        _lazyParams["first"] = 0;
        setLazyParams(_lazyParams);
    };

    const bỉrthDateTemplateBody = (rowData) => {
        return (
            <div className="flex align-items-center justify-content-center">
                <p>{rowData?.birthDate?.substr(0, 10)}</p>
            </div>
        );
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="flex align-items-center justify-content-center actions">
                <Button icon="pi pi-eye" className="p-button-rounded p-button mr-2" tooltip={t("view", { ns: "common" })} onClick={() => openDialog(rowData)} />
            </div>
        );
    };

    const openDialog = (data) => {
        setPersonInfoDetail({ ...data });
        setPersonInfoDialog(true);
    };

    const paginatorTemplate = {
        layout: "RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink",
        RowsPerPageDropdown: (options) => {
            const dropdownOptions = [
                { label: 10, value: 10 },
                { label: 20, value: 20 },
                { label: 30, value: 30 },
                { label: 50, value: 50 },
            ];

            return (
                <React.Fragment>
                    <div style={{ width: "140px", display: "inline-block" }}>
                        <span className="mx-1">{t("show", { ns: "common" })}: </span>
                        <Dropdown value={options.value} options={dropdownOptions} onChange={options.onChange} />
                    </div>
                </React.Fragment>
            );
        },
        CurrentPageReport: (options) => {
            return <span style={{ width: "calc(100% - 490px)", textAlign: "center", display: "inline-block" }}>{t("total", { ns: "common", total: options.totalRecords })}</span>;
        },
    };

    const searchItems = (event) => {
        //in a real application, make a request to a remote url with the query and return filtered results, for demo we filter at client side
        if (!event.query) {
            debounceSearchDivision(null);
        } else {
            debounceSearchDivision(event.query);
        }
    };

    const searchOption = (
        <>
            <div className="grid">
                {/* Số CCCD */}
                <div className="col-12 md:col-2">
                    <div className="grid custom-form">
                        <label
                            className="col-fixed text-center"
                            style={{
                                width: "100px",
                                // justifyContent: "end",
                                display: "flex",
                                padding: "0.75rem 0px 0px 5px",
                            }}
                            htmlFor="idNumber"
                        >
                            {t("person.idNumber")}
                        </label>
                        <div className="col">
                            <InputText
                                style={{
                                    width: "100%",
                                }}
                                id="idNumber"
                                name="idNumber"
                                onChange={(e) => {
                                    setCheckLoad(false);
                                    setLazyParams({
                                        ...lazyParams,
                                        filters: {
                                            ...lazyParams.filters,
                                            idNumber: {
                                                ...lazyParams.filters.idNumber,
                                                value: e.target.value,
                                            },
                                        },
                                    });
                                }}
                            ></InputText>
                        </div>
                    </div>
                </div>

                {/* Số điện thoại */}
                <div className="col-12 md:col-2">
                    <div className="grid custom-form">
                        <label
                            className="col-fixed text-center"
                            style={{
                                width: "100px",
                                // justifyContent: "end",
                                display: "flex",
                                padding: "0.75rem 0px 0px 5px",
                            }}
                            htmlFor="phoneNumber"
                        >
                            {t("person.phoneNumber")}
                        </label>
                        <div className="col">
                            <InputText
                                style={{
                                    width: "100%",
                                }}
                                id="phoneNumber"
                                name="phoneNumber"
                                onChange={(e) => {
                                    setCheckLoad(false);
                                    setLazyParams({
                                        ...lazyParams,
                                        filters: {
                                            ...lazyParams.filters,
                                            phoneNumber: {
                                                ...lazyParams.filters.phoneNumber,
                                                value: e.target.value,
                                            },
                                        },
                                    });
                                }}
                            ></InputText>
                        </div>
                    </div>
                </div>

                {/* họ và tên */}
                <div className="col-12 md:col-2">
                    <div className="grid custom-form">
                        <label
                            className="col-fixed text-center"
                            style={{
                                width: "130px",
                                // justifyContent: "end",
                                display: "flex",
                                padding: "0.75rem 0px 0px 5px",
                            }}
                            htmlFor="fullName"
                        >
                            {t("person.fullName")}
                        </label>
                        <div className="col">
                            <InputText
                                style={{
                                    width: "100%",
                                }}
                                id="fullName"
                                name="fullName"
                                onChange={(e) => {
                                    setCheckLoad(false);
                                    setLazyParams({
                                        ...lazyParams,
                                        filters: {
                                            ...lazyParams.filters,
                                            fullName: {
                                                ...lazyParams.filters.fullName,
                                                value: e.target.value,
                                            },
                                        },
                                    });
                                }}
                            ></InputText>
                        </div>
                    </div>
                </div>

                {/* ngày sinh  */}
                <div className="col-12 md:col-2">
                    <div className="grid custom-form">
                        <label
                            className="col-fixed text-center"
                            style={{
                                width: "130px",
                                // justifyContent: "end",
                                display: "flex",
                                padding: "0.75rem 0px 0px 5px",
                            }}
                            htmlFor="birthDate"
                        >
                            {t("person.birthDate")}
                        </label>
                        <div className="col">
                            <Calendar
                                style={{ width: "100%" }}
                                value={lazyParams.filters.birthDate.value}
                                onChange={(e) => {
                                    setLazyParams({
                                        ...lazyParams,
                                        filters: {
                                            ...lazyParams.filters,
                                            birthDate: {
                                                ...lazyParams.filters.birthDate,
                                                value: e.value,
                                            },
                                        },
                                    });
                                }}
                                onBlur={() => {
                                    setFormatDateVal(dateFormat1);
                                }}
                                onFocus={() => {
                                    setFormatDateVal(dateFormat2);
                                }}
                                name="birthDate"
                                id="birthDate"
                                dateFormat={formatDateVal}
                            ></Calendar>

                            {/* <InputText
                                style={{
                                    width: "100%",
                                }}
                                d
                                id="birthDate"
                                name="birthDate"
                                type="date"
                                onChange={(e) => {
                                    setCheckLoad(false);
                                    setLazyParams({
                                        ...lazyParams,
                                        filters: {
                                            ...lazyParams.filters,
                                            birthDate: {
                                                ...lazyParams.filters.birthDate,
                                                value: e.target.value,
                                            },
                                        },
                                    });
                                }}
                            ></InputText> */}
                        </div>
                    </div>
                </div>

                {/* địa chỉ thường trú */}
                <div className="col-12 md:col-3">
                    <div className="grid custom-form">
                        <label
                            className="col-fixed text-center"
                            style={{
                                width: "100px",
                                // justifyContent: "end",
                                display: "flex",
                                padding: "0.75rem 0.75rem 0px 0px",
                            }}
                            htmlFor="addressId"
                        >
                            {t("person.addressId")}
                        </label>
                        <AutoComplete
                            name="addressId"
                            className="col"
                            style={{ width: "100%", margin: "5px 0px 0px 0px" }}
                            id="addressId"
                            value={selectDivision}
                            suggestions={divisions}
                            completeMethod={searchItems}
                            field="label"
                            dropdown
                            onChange={(e) => {
                                setCheckLoad(false);
                                setSelectDivision(e.value);
                                setLazyParams({
                                    ...lazyParams,
                                    filters: {
                                        ...lazyParams.filters,
                                        addressId: {
                                            ...lazyParams.filters.addressId,
                                            value: e?.value?.value,
                                        },
                                    },
                                });
                            }}
                            dropdownAriaLabel="Chọn thường trú"
                            emptyMessage={"Không có dữ liệu"}
                            showEmptyMessage={true}
                            tooltip={divisions.find((e) => e.value === lazyParams.filters.addressId?.value)?.label}
                            tooltipOptions={{ position: "top" }}
                        />
                    </div>
                </div>

                <div className="col-12 md:col-1 text-right">
                    <div className="grid">
                        <div className="col-12">
                            <label
                                style={{
                                    display: "flex",
                                    padding: "0.75rem 0px 0px 0px",
                                }}
                            >
                                {" "}
                            </label>
                        </div>
                        <div className="col-12" style={{ margin: "5px 0px 0px 0px" }}>
                            <Button
                                label={t("search", { ns: "common" })}
                                icon="pi pi-search"
                                onClick={(e) => {
                                    if (
                                        lazyParams.filters.idNumber.value ||
                                        lazyParams.filters.idNumberOld.value ||
                                        lazyParams.filters.fatherIdNumber.value ||
                                        lazyParams.filters.motherIdNumber.value ||
                                        lazyParams.filters.phoneNumber.value ||
                                        (lazyParams.filters.fullName.value && lazyParams.filters.birthDate.value)
                                    ) {
                                        setCheckLoad(true);
                                        setLazyParams({
                                            ...lazyParams,
                                            first: 0,
                                            page: 0,
                                        });
                                    } else {
                                        if (lazyParams.filters.fromDate.value && lazyParams.filters.toDate.value) {
                                            if (checkDateMonth(lazyParams.filters.fromDate.value, lazyParams.filters.toDate.value, range2)) {
                                                setCheckLoad(true);
                                                setLazyParams({
                                                    ...lazyParams,
                                                    first: 0,
                                                    page: 0,
                                                });
                                            } else {
                                                toast.current.show({ severity: "info", summary: t("info", { ns: "common" }), detail: t("person.rangeFromToDate"), life: 3000 });
                                            }
                                        } else {
                                            toast.current.show({ severity: "info", summary: t("info", { ns: "common" }), detail: t("person.required2"), life: 3000 });
                                        }
                                    }

                                    // if (
                                    //     !lazyParams.filters.addressId.value &&
                                    //     !lazyParams.filters.birthDate.value &&
                                    //     !lazyParams.filters.fatherIdNumber.value &&
                                    //     !lazyParams.filters.fatherName.value &&
                                    //     !lazyParams.filters.fullName.value &&
                                    //     !lazyParams.filters.idNumber.value &&
                                    //     !lazyParams.filters.idNumberOld.value &&
                                    //     !lazyParams.filters.motherIdNumber.value &&
                                    //     !lazyParams.filters.motherName.value &&
                                    //     !lazyParams.filters.phoneNumber.value
                                    // ) {
                                    //     toast.current.show({ severity: "info", summary: t("info", { ns: "common" }), detail: t("person.required"), life: 3000 });
                                    // } else if (
                                    //     !!lazyParams.filters.idNumber.value ||
                                    //     !!lazyParams.filters.idNumberOld.value ||
                                    //     !!lazyParams.filters.fatherIdNumber.value ||
                                    //     !!lazyParams.filters.motherIdNumber.value ||
                                    //     // ho ten va ngay sinh
                                    //     (!!lazyParams.filters.fullName.value && !!lazyParams.filters.birthDate.value) ||
                                    //     // ho ten va ngay sinh va (ten cha hoặc tên me)
                                    //     (!!lazyParams.filters.fullName.value && !!lazyParams.filters.birthDate.value && (!!lazyParams.filters.motherName.value || !!lazyParams.filters.fatherName.value)) ||
                                    //     // dia chi thuong tru va 1 trong cac đk
                                    //     (!!lazyParams.filters.addressId.value &&
                                    //         (!!lazyParams.filters.birthDate.value ||
                                    //             !!lazyParams.filters.fatherIdNumber.value ||
                                    //             !!lazyParams.filters.fatherName.value ||
                                    //             !!lazyParams.filters.fullName.value ||
                                    //             !!lazyParams.filters.idNumber.value ||
                                    //             !!lazyParams.filters.idNumberOld.value ||
                                    //             !!lazyParams.filters.motherIdNumber.value ||
                                    //             !!lazyParams.filters.motherName.value ||
                                    //             !!lazyParams.filters.phoneNumber.value))
                                    // ) {
                                    //     setCheckLoad(true);
                                    //     setLazyParams({
                                    //         ...lazyParams,
                                    //         first: 0,
                                    //         page: 0,
                                    //     });
                                    // } else {
                                    //     toast.current.show({ severity: "info", summary: t("info", { ns: "common" }), detail: t("person.required1"), life: 3000 });
                                    // }
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="grid">
                {/* số CMND 9 số*/}
                <div className="col-12 md:col-4">
                    <div className="grid custom-form">
                        <label
                            className="col-fixed text-center"
                            style={{
                                width: "100px",
                                // justifyContent: "end",
                                display: "flex",
                                padding: "0.75rem 0px 0px 5px",
                            }}
                            htmlFor="idNumberOld"
                        >
                            {t("person.idNumberOld")}
                        </label>
                        <div className="col">
                            <InputText
                                style={{
                                    width: "100%",
                                }}
                                id="idNumberOld"
                                name="idNumberOld"
                                onChange={(e) => {
                                    setCheckLoad(false);
                                    setLazyParams({
                                        ...lazyParams,
                                        filters: {
                                            ...lazyParams.filters,
                                            idNumberOld: {
                                                ...lazyParams.filters.idNumberOld,
                                                value: e.target.value,
                                            },
                                        },
                                    });
                                }}
                            ></InputText>
                        </div>
                    </div>
                </div>

                {/* số cccd cha */}
                <div className="col-12 md:col-4">
                    <div className="grid custom-form">
                        <label
                            className="col-fixed text-center"
                            style={{
                                width: "100px",
                                // justifyContent: "end",
                                display: "flex",
                                padding: "0.75rem 0px 0px 5px",
                            }}
                            htmlFor="fatherIdNumber"
                        >
                            {t("person.fatherIdNumber")}
                        </label>
                        <div className="col">
                            <InputText
                                style={{
                                    width: "100%",
                                }}
                                id="fatherIdNumber"
                                name="fatherIdNumber"
                                onChange={(e) => {
                                    setCheckLoad(false);
                                    setLazyParams({
                                        ...lazyParams,
                                        filters: {
                                            ...lazyParams.filters,
                                            fatherIdNumber: {
                                                ...lazyParams.filters.fatherIdNumber,
                                                value: e.target.value,
                                            },
                                        },
                                    });
                                }}
                            ></InputText>
                        </div>
                    </div>
                </div>

                {/* họ và tên cha */}
                <div className="col-12 md:col-4">
                    <div className="grid custom-form">
                        <label
                            className="col-fixed text-center"
                            style={{
                                width: "100px",
                                // justifyContent: "end",
                                display: "flex",
                                padding: "0.75rem 0px 0px 5px",
                            }}
                            htmlFor="fatherName"
                        >
                            {t("person.fatherName")}
                        </label>
                        <div className="col">
                            <InputText
                                style={{
                                    width: "100%",
                                }}
                                id="fatherName"
                                name="fatherName"
                                onChange={(e) => {
                                    setCheckLoad(false);
                                    setLazyParams({
                                        ...lazyParams,
                                        filters: {
                                            ...lazyParams.filters,
                                            fatherName: {
                                                ...lazyParams.filters.fatherName,
                                                value: e.target.value,
                                            },
                                        },
                                    });
                                }}
                            ></InputText>
                        </div>
                    </div>
                </div>
            </div>
            <div className="grid">
                {/* số cccd mẹ */}
                <div className="col-12 md:col-3">
                    <div className="grid custom-form">
                        <label
                            className="col-fixed text-center"
                            style={{
                                width: "100px",
                                // justifyContent: "end",
                                display: "flex",
                                padding: "0.75rem 0px 0px 5px",
                            }}
                            htmlFor="motherIdNumber"
                        >
                            {t("person.motherIdNumber")}
                        </label>
                        <div className="col">
                            <InputText
                                style={{
                                    width: "100%",
                                }}
                                id="motherIdNumber"
                                name="motherIdNumber"
                                onChange={(e) => {
                                    setCheckLoad(false);
                                    setLazyParams({
                                        ...lazyParams,
                                        filters: {
                                            ...lazyParams.filters,
                                            motherIdNumber: {
                                                ...lazyParams.filters.motherIdNumber,
                                                value: e.target.value,
                                            },
                                        },
                                    });
                                }}
                            ></InputText>
                        </div>
                    </div>
                </div>

                {/* họ và tên mẹ */}
                <div className="col-12 md:col-3">
                    <div className="grid custom-form">
                        <label
                            className="col-fixed text-center"
                            style={{
                                width: "100px",
                                // justifyContent: "end",
                                display: "flex",
                                padding: "0.75rem 0px 0px 5px",
                            }}
                            htmlFor="motherName"
                        >
                            {t("person.motherName")}
                        </label>
                        <div className="col">
                            <InputText
                                style={{
                                    width: "100%",
                                }}
                                id="motherName"
                                name="motherName"
                                onChange={(e) => {
                                    setCheckLoad(false);
                                    setLazyParams({
                                        ...lazyParams,
                                        filters: {
                                            ...lazyParams.filters,
                                            motherName: {
                                                ...lazyParams.filters.motherName,
                                                value: e.target.value,
                                            },
                                        },
                                    });
                                }}
                            ></InputText>
                        </div>
                    </div>
                </div>

                {/* Ngày nhận từ */}
                <div className="col-12 md:col-3">
                    <div className="grid custom-form">
                        <label
                            className="col-fixed text-center"
                            style={{
                                width: "100px",
                                // justifyContent: "end",
                                display: "flex",
                                padding: "0.75rem 0px 0px 5px",
                            }}
                            htmlFor="fromDate"
                        >
                            {t("person.fromDate")}
                        </label>
                        <div className="col">
                            <Calendar
                                style={{ width: "100%" }}
                                value={lazyParams.filters.fromDate.value}
                                onChange={(e) => {
                                    setLazyParams({
                                        ...lazyParams,
                                        filters: {
                                            ...lazyParams.filters,
                                            fromDate: {
                                                ...lazyParams.filters.fromDate,
                                                value: e.value,
                                            },
                                        },
                                    });
                                }}
                                onBlur={() => {
                                    setFormatDateVal(dateFormat1);
                                }}
                                onFocus={() => {
                                    setFormatDateVal(dateFormat2);
                                }}
                                name="fromDate"
                                id="fromDate"
                                maxDate={lazyParams.filters.toDate.value}
                                dateFormat={formatDateVal}
                            ></Calendar>
                            {/* <InputText
                                style={{
                                    width: "100%",
                                }}
                                type="date"
                                id="fromDate"
                                name="fromDate"
                                value={lazyParams.filters.fromDate.value}
                                max={lazyParams.filters.toDate.value}
                                onChange={(e) => {
                                    setCheckLoad(false);
                                    setLazyParams({
                                        ...lazyParams,
                                        filters: {
                                            ...lazyParams.filters,
                                            fromDate: {
                                                ...lazyParams.filters.fromDate,
                                                value: e.target.value,
                                            },
                                        },
                                    });
                                }}
                            ></InputText> */}
                        </div>
                    </div>
                </div>

                {/* đến */}
                <div className="col-12 md:col-3">
                    <div className="grid custom-form">
                        <label
                            className="col-fixed text-center"
                            style={{
                                width: "130px",
                                // justifyContent: "end",
                                display: "flex",
                                padding: "0.75rem 0px 0px 5px",
                            }}
                            htmlFor="toDate"
                        >
                            {t("person.toDate")}
                        </label>
                        <div className="col">
                            <Calendar
                                style={{ width: "100%" }}
                                value={lazyParams.filters.toDate.value}
                                onChange={(e) => {
                                    setLazyParams({
                                        ...lazyParams,
                                        filters: {
                                            ...lazyParams.filters,
                                            toDate: {
                                                ...lazyParams.filters.toDate,
                                                value: e.value,
                                            },
                                        },
                                    });
                                }}
                                onBlur={() => {
                                    setFormatDateVal(dateFormat1);
                                }}
                                onFocus={() => {
                                    setFormatDateVal(dateFormat2);
                                }}
                                name="toDate"
                                id="toDate"
                                // maxDate={now}
                                minDate={lazyParams.filters.fromDate.value}
                                dateFormat={formatDateVal}
                            ></Calendar>
                            {/* <InputText
                                style={{
                                    width: "100%",
                                }}
                                type="date"
                                id="toDate"
                                name="toDate"
                                value={lazyParams.filters.toDate.value}
                                max={now}
                                min={lazyParams.filters.fromDate.value}
                                onChange={(e) => {
                                    setCheckLoad(false);
                                    setLazyParams({
                                        ...lazyParams,
                                        filters: {
                                            ...lazyParams.filters,
                                            toDate: {
                                                ...lazyParams.filters.toDate,
                                                value: e.target.value,
                                            },
                                        },
                                    });
                                }}
                            ></InputText> */}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );

    return (
        <div className="grid crud">
            <div className="col-12">
                <div className="card">
                    <div className="grid">
                        <div className="col-12">
                            <h4 className="text-center font-bold">{t("person.title")?.toUpperCase()}</h4>
                        </div>
                    </div>
                    <div className="grid">
                        <div className="col-12">
                            <BlockDetail title={t("search", { ns: "common" })}>{searchOption}</BlockDetail>
                        </div>
                    </div>

                    <div className="grid">
                        <div className="col-12">
                            <BlockDetail title={t("person.list")}>
                                <Toast ref={toast} />
                                <DataTable
                                    ref={dt}
                                    value={personInfos}
                                    dataKey="stt"
                                    className="datatable-responsive p-datatable-sm"
                                    selectionMode="single"
                                    selection={selectedPerson}
                                    onSelectionChange={(e) => {
                                        setSelectedPerson(e.value);
                                    }}
                                    showGridlines
                                    lazy
                                    paginator
                                    rows={lazyParams.rows}
                                    first={lazyParams.first}
                                    totalRecords={totalRecords}
                                    onPage={onPage}
                                    onSort={onSort}
                                    sortField={lazyParams.sortField}
                                    sortOrder={lazyParams.sortOrder}
                                    onFilter={onFilter}
                                    filters={lazyParams.filters}
                                    loading={loading}
                                    paginatorClassName="justify-content-start"
                                    paginatorTemplate={paginatorTemplate}
                                    emptyMessage="Không có dữ liệu"
                                    scrollable
                                >
                                    <Column field="stt" header={t("stt", { ns: "common" })} style={{ maxWidth: "50px" }} />
                                    <Column field="idNumber" header={t("person.idNumber")} style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="fullName" header={t("person.fullName")} style={{ minWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="birthDate" header={t("person.birthDate")} body={bỉrthDateTemplateBody} style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="gender" header={t("person.gender")} style={{ minWidth: "100px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }}></Column>
                                    <Column field="phoneNumber" header={t("person.phoneNumber")} style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="address" header={t("person.address")} style={{ minWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="idNumberOld" header={t("person.idNumberOld")} style={{ minWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="fatherName" header={t("person.fatherName")} style={{ minWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="fatherIdNumber" header={t("person.fatherIdNumber")} style={{ minWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="motherName" header={t("person.motherName")} style={{ minWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="motherIdNumber" header={t("person.motherIdNumber")} style={{ minWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column body={actionBodyTemplate} style={{ maxWidth: "100px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                </DataTable>
                            </BlockDetail>

                            {selectedPerson && (
                                <div className="grid mt-2">
                                    <div className="col-12" style={{ textAlign: "center" }}>
                                        <Button
                                            style={{ height: "40px", margin: "2px" }}
                                            label={t("person.historyChangeInfo")}
                                            onClick={(e) => {
                                                setChangeType(1);
                                                setPersonInfoHistoryDialog(true);
                                            }}
                                        />
                                    </div>
                                </div>
                            )}

                            {personInfo && (
                                <div className="grid ">
                                    <div className="col-12">
                                        <BlockDetail title={t("person.list1")}>
                                            <div className="grid">
                                                <div className="col-fixed" style={{ width: "200px" }}>
                                                    <img src={personInfo.avatar ? `data:image/jpeg;base64,${personInfo.avatar}` : avatar} width="100%" height={"300px"} alt=""></img>
                                                </div>
                                                <div className="col">
                                                    <div className="grid">
                                                        <div className="col-12">
                                                            <div className="grid">
                                                                {/* số cccd */}
                                                                <div className="field col-12 md:col-2">
                                                                    <label htmlFor="idNumber" className="custom-form">
                                                                        {t("person.idNumber")}
                                                                    </label>
                                                                    <InputText id="idNumber" value={personInfo?.idNumber} readOnly className="custom-form" />
                                                                </div>
                                                                {/* họ và tên */}
                                                                <div className="field col-12 md:col-2">
                                                                    <label htmlFor="fullName2" className="custom-form">
                                                                        {t("person.fullName2")}
                                                                    </label>
                                                                    <InputText id="fullName2" value={personInfo?.fullName2} readOnly className="custom-form" />
                                                                </div>

                                                                {/* ngày sinh */}
                                                                <div className="field col-12 md:col-2">
                                                                    <label htmlFor="birthdate" className="custom-form">
                                                                        {t("person.birthdate")}
                                                                    </label>
                                                                    <InputText id="birthdate" value={personInfo?.birthdate} readOnly className="custom-form" />
                                                                </div>

                                                                {/* giới tính */}
                                                                <div className="field col-12 md:col-2">
                                                                    <label htmlFor="gender" className="custom-form">
                                                                        {t("person.gender")}
                                                                    </label>
                                                                    <InputText id="gender" value={personInfo?.gender} readOnly className="custom-form" />
                                                                </div>

                                                                {/* ngày cấp */}
                                                                <div className="field col-12 md:col-2">
                                                                    <label htmlFor="issuedOn" className="custom-form">
                                                                        {t("person.issuedOn")}
                                                                    </label>
                                                                    <InputText id="issuedOn" value={personInfo?.issuedOn} readOnly className="custom-form" />
                                                                </div>

                                                                {/* ngày hết hạn */}
                                                                <div className="field col-12 md:col-2">
                                                                    <label htmlFor="expirationDate" className="custom-form">
                                                                        {t("person.expirationDate")}
                                                                    </label>
                                                                    <InputText id="expirationDate" value={personInfo?.expirationDate} readOnly className="custom-form" />
                                                                </div>
                                                            </div>

                                                            <div className="grid">
                                                                {/* quê quán */}
                                                                <div className="field col-12 md:col-6">
                                                                    <label htmlFor="permanentAddress2" className="custom-form">
                                                                        {t("person.permanentAddress2")}
                                                                    </label>
                                                                    <InputText
                                                                        id="permanentAddress2"
                                                                        value={(personInfo?.nativePlace1 ? personInfo?.nativePlace1 + ", " : "") + (personInfo?.nativePlace2 ? personInfo?.nativePlace2 : "")}
                                                                        readOnly
                                                                        className="custom-form"
                                                                        tooltip={(personInfo?.nativePlace1 ? personInfo?.nativePlace1 + ", " : "") + (personInfo?.nativePlace2 ? personInfo?.nativePlace2 : "")}
                                                                        tooltipOptions={{ position: "top" }}
                                                                    />
                                                                </div>

                                                                {/* nơi thường trú */}
                                                                <div className="field col-12 md:col-6">
                                                                    <label htmlFor="nativePlace2" className="custom-form">
                                                                        {t("person.nativePlace2")}
                                                                    </label>
                                                                    <InputText
                                                                        id="nativePlace2"
                                                                        value={(personInfo?.permanentAddress1 ? personInfo?.permanentAddress1 + ", " : "") + (personInfo?.permanentAddress2 ? personInfo?.permanentAddress2 : "")}
                                                                        readOnly
                                                                        className="custom-form"
                                                                        tooltip={(personInfo?.permanentAddress1 ? personInfo?.permanentAddress1 + ", " : "") + (personInfo?.permanentAddress2 ? personInfo?.permanentAddress2 : "")}
                                                                        tooltipOptions={{ position: "top" }}
                                                                    />
                                                                </div>
                                                            </div>

                                                            {/* đặc điểm dị hình */}
                                                            <div className="grid">
                                                                <div className="field col-12 md:col-6">
                                                                    <label htmlFor="identification2" className="custom-form">
                                                                        {t("person.identification2")}
                                                                    </label>
                                                                    <InputText
                                                                        id="identification2"
                                                                        value={(personInfo?.identification1 ? personInfo?.identification1 : "") + (personInfo?.identification2 ? personInfo?.identification2 : "")}
                                                                        readOnly
                                                                        className="custom-form"
                                                                        tooltip={(personInfo?.identification1 ? personInfo?.identification1 : "") + (personInfo?.identification2 ? personInfo?.identification2 : "")}
                                                                        tooltipOptions={{ position: "top" }}
                                                                    />
                                                                </div>

                                                                <div className="field col-12 md:col-6" style={{ display: "flex", justifyContent: "center", margin: "auto" }}>
                                                                    <Button
                                                                        style={{ height: "40px", margin: "2px" }}
                                                                        label={t("person.historyChangeCCCD")}
                                                                        onClick={(e) => {
                                                                            setChangeType(4);
                                                                            setPersonInfoHistoryDialog(true);
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </BlockDetail>
                                    </div>
                                </div>
                            )}

                            <PersonInfoEditor />

                            <PersonInfoHistory />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
